import { canPromoteInPlace } from "../fundedLineage";
import {
  useQuery,
  getPayoutSuggestions,
  getAccountResetSuggestions,
  getTradovateConnections,
} from "wasp/client/operations";
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  Edit,
  ExternalLink,
  MoreHorizontal,
  Power,
  PowerOff,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "../../client/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../client/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../client/components/ui/tooltip";
import useTranslation from "../../client/hooks/useTranslation";
import { CURRENCY } from "../../shared/currency";
import AccountSourceBadge from "../AccountSourceBadge";
import { deactivationBadge, deactivationLabels } from "../deactivationBadge";
import { formatViolationLabel } from "../formatViolationLabel";
import { buildViolationDetails } from "../violationLabels";
import { resolveBounds } from "../accountProgressGeometry";
import { resolveConsistencyRule } from "../propFirmEngine";
import { AccountProgressBar, type AxisTone } from "./AccountProgressBar";
import { resolveSyncPresentation } from "../tradovate/tradovateSyncPresentation";
import type { AccountStatus, EnrichedAccount } from "../accountTypes";
// Symboles partagés avec la page, sortis dans un module neutre : les
// importer depuis la page créerait un cycle (elle importe cette card).
import { PropFirmBadge, STATUS_STYLES, formatCurrency } from "../accountCardShared";
import { isPropFirmFundedStatus } from "../accountStatus";

export function AccountCard({
  account,
  onEdit,
  onDelete,
  onToggleActive,
  onRequestDeactivate,
  onOpenDetail,
  onFundedTransition,
  // Toujours déclarés : la page les passe encore. La card ne porte plus le
  // bouton de connexion MetaAPI, mais retirer les props casserait l'appelant.
  isPro: _isPro,
  onConnect: _onConnect,
}: {
  account: EnrichedAccount;
  onEdit: (a: EnrichedAccount) => void;
  onDelete: (a: EnrichedAccount) => void;
  onToggleActive: (a: EnrichedAccount) => void;
  onRequestDeactivate: (a: EnrichedAccount) => void;
  onOpenDetail: (a: EnrichedAccount) => void;
  onFundedTransition: (a: EnrichedAccount) => void;
  isPro?: boolean;
  onConnect?: (accountId: string) => void;
}) {
  const t = useTranslation();
  const pnl = account.currentBalance - account.initialBalance;
  const isPositive = pnl >= 0;

  const inheritedTemplate = !account.propFirmTemplateId && account.group?.propFirmTemplate != null;
  const effectiveTemplate = account.effectiveTemplate;


  // Tradovate link comes from the enriched account (tradovateSyncedAccount).
  // The sync status itself is connection-scoped (one connection per prop firm).
  const tradovateConnectionId = (account as any).tradovateSyncedAccount?.connectionId as string | undefined;
  const isTradovateSynced = !!tradovateConnectionId;

  // La card ne porte plus le bloc de sync, mais garde son ALARME : une
  // connexion cassée doit rester visible sans ouvrir le détail.
  //
  // ⚠️ `displayStatus` et `realtimeStatus` NE SONT PAS des colonnes : le
  // serveur les DÉRIVE dans `getTradovateConnections` (computeDisplayStatus +
  // computeRealtimeStatus, qui lit les baux socket). Les chercher sur
  // `account.tradovateSyncedAccount.connection` rendrait la pastille
  // silencieusement morte. On lit donc la même requête que
  // TradovateSyncStatus : user-scoped et sans argument, donc dédupliquée par
  // react-query entre toutes les cartes, exactement comme les suggestions.
  const { data: tradovateConnections = [] } = useQuery(getTradovateConnections, {});
  const tradovateConnection = isTradovateSynced
    ? (tradovateConnections as any[]).find((c) => c.id === tradovateConnectionId) ?? null
    : null;
  const syncTone = tradovateConnection
    ? resolveSyncPresentation({
        displayStatus: tradovateConnection.displayStatus ?? tradovateConnection.status,
        realtimeStatus: tradovateConnection.realtimeStatus,
      }).tone
    : null;
  const syncNeedsAttention = syncTone === "warning" || syncTone === "critical";

  // Pending payout suggestions → discreet dot on the card (user-scoped query,
  // deduped by react-query across all cards).
  const { data: payoutSuggestions = [] } = useQuery(getPayoutSuggestions, {});
  const hasPayoutSuggestion = (payoutSuggestions as any[]).some(
    (s) => s.accountId === account.id,
  );

  // Idem pour les resets suggérés : requête user-scoped (sans accountId) donc
  // dédupliquée par react-query entre toutes les cartes, une seule requête HTTP.
  const { data: resetSuggestions = [] } = useQuery(getAccountResetSuggestions, {});
  const hasResetSuggestion = (resetSuggestions as any[]).some(
    (s) => s.accountId === account.id,
  );

  const buffer = account.bufferStatus;
  const challenge = account.challengeProgress;
  const payout = account.payoutProgress;

  const isFunded = isPropFirmFundedStatus(account.status, !!account.effectiveTemplate);
  // Même résolution que le moteur d'alertes : sinon la card afficherait une
  // règle et l'alerte en appliquerait une autre.
  const consistencyPercent = resolveConsistencyRule(
    account.activeTier,
    account.resolvedPhase ?? null,
    account.status,
    account.consistencyRulePercent,
  );

  // L'axe ne se dessine que si TOUTES ses bornes sont résolues. Le repli sur
  // « balance seule » n'est pas un cas dégradé à contourner : une borne
  // inventée sur un axe de drawdown affirmerait une marge qui n'existe pas.
  const axisBounds = resolveBounds({
    status: account.status,
    initialBalance: account.initialBalance,
    objectiveBase: account.activeTier?.accountSize ?? account.initialBalance,
    buffer: buffer ?? null,
    challengeTargetAmount: challenge?.targetAmount ?? null,
    payoutTargetAmount: payout?.targetAmount ?? null,
  });

  const axisTone: AxisTone = buffer?.isViolation
    ? "violation"
    : buffer?.isWarning
      ? "warning"
      : "normal";

  const violationEntries = account.ruleViolations ? Object.values(account.ruleViolations) : [];
  const accountHasViolation = violationEntries.some((v) => v.types?.some((tp) => tp.endsWith("_violation")));
  const accountHasWarning = !accountHasViolation && violationEntries.some((v) => v.types?.some((tp) => tp.endsWith("_warning")));
  // Libellés CHIFFRÉS : le pourcentage atteint et le seuil, pour que la règle
  // soit vérifiable plutôt que simplement assénée.
  const accountViolationLabels = buildViolationDetails(violationEntries as any)
    .map((d) =>
      formatViolationLabel(d, {
        dailyLossViolation: t.tradingAccounts.violationDailyLoss,
        dailyLossWarning: t.tradingAccounts.warningDailyLoss,
        consistencyViolation: t.tradingAccounts.violationConsistency,
        consistencyWarning: t.tradingAccounts.warningConsistency,
        consistencyViolationDetail: t.tradingAccounts.violationConsistencyDetail,
        consistencyWarningDetail: t.tradingAccounts.warningConsistencyDetail,
      }),
    )
    .filter((v, i, a) => a.indexOf(v) === i);

  const statusStyle = STATUS_STYLES[account.status] ?? STATUS_STYLES.inactive;

  // Status label
  const statusLabels: Record<AccountStatus, string> = {
    challenge: t.tradingAccounts.statusChallenge,
    pa: t.tradingAccounts.statusPa,
    inactive: t.tradingAccounts.statusInactive,
    demo: t.tradingAccounts.statusDemo,
    live: t.tradingAccounts.statusLive,
  };

  // Card border based on state
  const cardBorder = !account.isActive
    ? "border-dashed border-border/40 opacity-60"
    : buffer?.isViolation
      ? "border-red-400/60 dark:border-red-700/60"
      : buffer?.isWarning
        ? "border-yellow-400/60 dark:border-yellow-600/60"
        : "border-border hover:border-primary/30";

  return (
    <div
      className={`relative rounded-xl border bg-card transition-all cursor-pointer group overflow-hidden ${cardBorder}`}
      onClick={() => onOpenDetail(account)}
    >
      {/* Colored top strip based on status */}
      <div className={`h-0.5 w-full ${statusStyle.dot}`} />

      <div className="p-3 space-y-3">
        {/* ── Row 1: Name + actions ── */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 space-y-1">
            {/* Status pill + propfirm badge */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${statusStyle.label}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                {statusLabels[account.status]}
              </span>
              {effectiveTemplate && (
                <PropFirmBadge template={effectiveTemplate} inherited={inheritedTemplate} size="xs" />
              )}
              {account.activeTier && (
                <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded-sm">
                  {account.activeTier.label}
                </span>
              )}
              <AccountSourceBadge account={account as any} />
              {buffer?.isViolation && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-pnlNegative">
                  <AlertCircle className="h-3 w-3" />
                  {t.tradingAccounts.maxLossExceeded}
                </span>
              )}
              {syncNeedsAttention && (
                <span
                  title={t.tradingAccounts.syncNeedsAttention}
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning"
                />
              )}
              {!account.isActive &&
                (() => {
                  const badge = deactivationBadge(
                    (account as any).deactivatedReason,
                    deactivationLabels(t),
                  );
                  if (!badge) return null;
                  return (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground"
                      title={badge.hint}
                    >
                      {badge.label}
                    </span>
                  );
                })()}
            </div>
            {/* Account name + violation icon */}
            <div className="flex items-center gap-1">
              <p className="font-semibold text-sm truncate leading-tight">{account.name}</p>
              {hasPayoutSuggestion && (
                <span
                  title={t.tradingAccounts.payoutSuggestionLabel}
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                />
              )}
              {hasResetSuggestion && (
                <span
                  title={t.tradingAccounts.accountResetSuggestionLabel}
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                />
              )}
              {(accountHasViolation || accountHasWarning) && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="shrink-0 cursor-default">
                        {accountHasViolation
                          ? <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                          : <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />
                        }
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[220px]">
                      <ul className="space-y-0.5">
                        {accountViolationLabels.map((label, i) => (
                          <li key={i}>{label}</li>
                        ))}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          {/* Actions dropdown + quick deactivate */}
          <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => onOpenDetail(account)}>
                  <ExternalLink className="h-3.5 w-3.5 mr-2" />
                  {t.tradingAccounts.accountDetails}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(account)}>
                  <Edit className="h-3.5 w-3.5 mr-2" />
                  {t.tradingAccounts.editAccountTitle}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onToggleActive(account)}>
                  {account.isActive
                    ? <><PowerOff className="h-3.5 w-3.5 mr-2" />{t.tradingAccounts.deactivateAccount}</>
                    : <><Power className="h-3.5 w-3.5 mr-2" />{t.tradingAccounts.activateAccount}</>
                  }
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onDelete(account)} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  {t.tradingAccounts.confirmDelete}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {account.isActive ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                title={t.tradingAccounts.deactivateAccount}
                onClick={() => onRequestDeactivate(account)}
              >
                <X className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                title={t.tradingAccounts.activateAccount}
                onClick={() => onToggleActive(account)}
              >
                <Power className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* ── Balance + PnL (sans pourcentage) ── */}
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
              {t.tradingAccounts.currentBalance}
            </p>
            <p className="text-base font-bold font-mono leading-none">
              {formatCurrency(account.currentBalance, CURRENCY)}
            </p>
          </div>
          <p className={`text-sm font-bold font-mono shrink-0 ${isPositive ? "text-pnlPositive" : "text-pnlNegative"}`}>
            {isPositive ? "+" : ""}{formatCurrency(pnl, CURRENCY)}
          </p>
        </div>

        {/* ── L'axe, quand ses bornes sont résolues ── */}
        {axisBounds && (
          <AccountProgressBar
            bounds={axisBounds}
            currentBalance={account.currentBalance}
            currency={CURRENCY}
            tone={axisTone}
            size="sm"
          />
        )}

        {/* ── Bouton de passage en funded ── */}
        {canPromoteInPlace(account) && challenge && challenge.ratio >= 1 && (
          <Button
            size="sm"
            className="w-full"
            onClick={(e) => { e.stopPropagation(); onFundedTransition(account); }}
          >
            {t.tradingAccounts.promoteToFunded}
          </Button>
        )}

        {/* ── Pied : trades + consistance (financé) + jours tradés ── */}
        <div className="flex items-center justify-between gap-x-3 gap-y-1 flex-wrap text-[10px] text-muted-foreground pt-0.5 border-t border-border/50">
          <span className="flex items-center gap-x-2 gap-y-1 flex-wrap min-w-0">
            <span>
              {account.tradesCount} trade{account.tradesCount !== 1 ? "s" : ""}
            </span>
            {/* La règle de consistance est la contrainte qui décide d'un
                retrait sur un compte financé : elle se lit avec les jours
                tradés, pas seulement dans la fiche détaillée. Libellé COURT,
                la card est trop étroite pour « Max X% / jour ». */}
            {isFunded && consistencyPercent !== null && (
              <span className="font-mono truncate">
                {t.tradingAccounts.consistencyShort.replace(
                  "{percent}",
                  String(consistencyPercent),
                )}
              </span>
            )}
          </span>
          {challenge && challenge.daysTraded > 0 && (
            <span className="flex items-center gap-1 font-mono">
              <Calendar className="h-3 w-3" />
              {challenge.minTradingDays !== null
                ? `${challenge.daysTraded} / ${challenge.minTradingDays}`
                : t.tradingAccounts.daysTradedCount.replace("{count}", String(challenge.daysTraded))}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

